import { useSyncExternalStore } from 'react';

export interface SessionUser {
  userId: string;
  username: string;
  email: string;
  roles: string[];
}

export interface Session {
  accessToken: string;
  refreshToken: string;
  user: SessionUser;
}

export type EndReason = 'signed-out' | 'expired';

const KEY = 'rq-session';
const listeners = new Set<() => void>();

function read(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function write(value: Session | null) {
  try {
    if (value) localStorage.setItem(KEY, JSON.stringify(value));
    else localStorage.removeItem(KEY);
  } catch {
    // Private mode or blocked storage: the session lives only in memory.
  }
}

let current = read();
let lastEnd: EndReason | null = null;
// Moves on every sign-in and sign-out, so a refresh that lands afterwards can't revive a session.
let epoch = 0;

function emit() {
  listeners.forEach((listener) => listener());
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    current = read();
    epoch++;
    emit();
  });
}

export const session = {
  get: (): Session | null => current,
  epoch: (): number => epoch,

  start(value: Session) {
    current = value;
    lastEnd = null;
    epoch++;
    write(value);
    emit();
  },

  rotate(accessToken: string, refreshToken: string) {
    if (!current) return;
    current = { ...current, accessToken, refreshToken };
    write(current);
    emit();
  },

  end(reason: EndReason) {
    if (!current) return;
    current = null;
    lastEnd = reason;
    epoch++;
    write(null);
    emit();
  },

  /** Why the last session ended, read once by the sign-in page. */
  takeEndReason(): EndReason | null {
    const reason = lastEnd;
    lastEnd = null;
    return reason;
  },

  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export const isAdmin = (value: Session | null): boolean =>
  value?.user.roles.includes('Admin') ?? false;

export function useSession(): Session | null {
  return useSyncExternalStore(session.subscribe, session.get, session.get);
}
